"use client";

import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

import { SubmitButton } from "@/components/SubmitButton";

/** A stable serialization of a form's fields, for change detection. */
function snapshot(form: HTMLFormElement): string {
  return Array.from(
    new FormData(form).entries(),
    ([k, v]) => `${k}=${String(v)}`,
  ).join("&");
}

/**
 * Submit button for the list-page filter toolbars (GET forms). It disables
 * itself whenever the form matches the state it was rendered with — i.e. the
 * filter already applied to the current results — so a click can't push a
 * redundant (or empty `?q=`) entry into history. It enables the moment any
 * field differs: the search box, a filter select, anything. Tracking the whole
 * enclosing form means one button covers every field with no per-field wiring.
 *
 * Renders the house solid pill through SubmitButton so every toolbar reads
 * identically ("fix once, fix everywhere") — including the disabled grey, which
 * is the kit's own (rainbowRingClass) treatment rather than a copy of it.
 * Pressing Enter in the search box still submits natively, so the toolbar keeps
 * working even before this hydrates.
 */
export function FilterSubmit({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const baseline = useRef("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const form = ref.current?.form;
    if (!form) return;
    baseline.current = snapshot(form);
    const sync = () => setDirty(snapshot(form) !== baseline.current);
    form.addEventListener("input", sync);
    form.addEventListener("change", sync);
    return () => {
      form.removeEventListener("input", sync);
      form.removeEventListener("change", sync);
    };
  }, []);

  return (
    <SubmitButton
      ref={ref}
      variant="solid"
      disabled={!dirty}
      className={className}
    >
      {children}
    </SubmitButton>
  );
}
